import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import { FeedsDataItem, FeedsListAction, FeedsListStoreHook } from '../feeds_list_types';
import { feedsDataAtom, feedsPageStateAtom, feedsUserContextAtom } from '../feeds_list_atoms';
import { useCallback } from 'react';
import feedsRepo from '../../data/feedsRepo';
import { tableDataVM } from '../../../../core/table/table_view_models';
import { OpsFeedsSearchModel } from '../search/feeds_search_types';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import { getDefaultStore } from 'jotai';
import { feedsSearchFormAtom } from '../search/feeds_search_atoms';
import feedsListApiToVM from '../table/feedsListApiToVM';
import { useAtomValue } from 'jotai';

type FeedsStoreActions = Pick<FeedsListStoreHook, 'getList' | 'setAction'>;

const useFeedsStoreActionsCore = (): FeedsStoreActions => {
    const setState = useSetImmerAtom(feedsPageStateAtom);
    const setList = useSetImmerAtom(feedsDataAtom);
    const { userType } = useAtomValue(feedsUserContextAtom);

    const getListAsync = useCallback(
        async (query: OpsFeedsSearchModel) => {
            try {
                ngDelegate.wait(true, true);
                setState(draft => {
                    draft.loading = true;
                    draft.action = 'QUERY';
                    return draft;
                });
                const res = await feedsRepo.getList(query, userType === 'CE');
                const cleanedRes = feedsListApiToVM(res);

                // success: update page state and table data in one write each
                setState(draft => {
                    draft.action = 'LIST';
                    draft.loading = false;
                    return draft;
                });
                setList(() => tableDataVM<FeedsDataItem>(cleanedRes as FeedsDataItem[]));
                ngDelegate.wait(false);
            } catch (err: any) {
                setList(() => tableDataVM<FeedsDataItem>());
                setState(draft => {
                    draft.loading = false;
                    return draft;
                });
                ngDelegate.wait(false);
            }
        },
        [setList, setState, userType],
    );

    const getList = useCallback(
        (query?: OpsFeedsSearchModel): void => {
            if (query) {
                getListAsync(query);
                return;
            }

            // Read current form state on demand to avoid subscribing this hook to every form edit.
            const defaultStore = getDefaultStore();
            const searchForm = defaultStore.get<OpsFeedsSearchModel>(feedsSearchFormAtom);
            getListAsync(searchForm);
        },
        [getListAsync],
    );

    const setAction = useCallback(
        (action: FeedsListAction): void => {
            // used by QUERY to open the search form and hide the table
            setState(draft => {
                draft.action = action;
                return draft;
            });
        },
        [setState],
    );

    return {
        getList,
        setAction,
    };
};

export const useFeedsStoreActions = (): FeedsStoreActions => {
    return useFeedsStoreActionsCore();
};

export const useFeedsStore = (): FeedsListStoreHook => {
    const [state] = useImmerAtom(feedsPageStateAtom);
    const { getList, setAction } = useFeedsStoreActionsCore();

    return {
        action: state.action,
        loading: state.loading,
        getList,
        setAction,
    };
};
